"use client";

import { Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  question: "How do OTA strategies boost revenue?",
  answer: "We optimize your presence on platforms like Booking.com and Expedia by refining rate parity, managing inventory availability, and leveraging promotional tools. This increases visibility and conversion, directly driving higher net revenue for your property."
}, {
  question: "What tech stack do you recommend?",
  answer: "We evaluate your current operations to suggest integrated Property Management Systems (PMS), Channel Managers, and Revenue Management Systems (RMS). Our goal is to automate manual tasks and provide real-time data for smarter pricing decisions."
}, {
  question: "What is included in a revenue audit?",
  answer: "Our audit covers a deep dive into your historical performance, competitor benchmarking, pricing strategy, and distribution channel efficiency. You receive a comprehensive report with actionable steps to capture lost revenue opportunities."
}, {
  question: "How long until I see results?",
  answer: "Revenue management is a continuous process. While some tactical adjustments yield immediate improvements in booking pace, significant shifts in RevPAR and market share typically become evident within 30 to 60 days of implementation."
}, {
  question: "Do you work with small hotels?",
  answer: "Yes. We specialize in helping independent hotels and boutique properties compete with larger chains. Our strategies are scalable and tailored to the specific needs and budget of your unique property."
}, {
  question: "How do we start a consultation?",
  answer: "Simply fill out our contact form to schedule an initial discovery call. We will discuss your current challenges, revenue goals, and how our data-driven approach can help you maximize your hotel's profitability."
}, {
  question: "Is my data kept confidential?",
  answer: "Absolutely. We treat your property data with the highest level of professional integrity. All insights and strategies are developed within a secure framework, ensuring your competitive advantage remains protected."
}, {
  question: "What is your pricing model?",
  answer: "We offer flexible engagement models, including project-based audits and monthly retainer services for ongoing revenue management. We discuss the best fit for your property during our initial consultation."
}];
export default function Faq() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2463" id="otamanagementfaq" className="bg-background text-foreground py-24 md:py-32 lg:py-40 px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="lg:sticky lg:top-24">
              <div className="font-mono text-xs tracking-widest uppercase text-primary mb-8">
                REVENUE INSIGHTS / 03
              </div>

              <h2 className="font-default font-bold text-foreground mb-10" style={{
              fontSize: "clamp(2.75rem, 5vw, 5rem)",
              lineHeight: "0.95",
              letterSpacing: "-0.03em"
            }}>
                Common
                <br />
                Revenue
                <br />
                Queries.
              </h2>

              <p className="font-sans text-base md:text-lg leading-relaxed mb-10" style={{
              maxWidth: "360px",
              color: "hsl(215 16% 40%)"
            }}>
                We answer the most frequent questions from hotel owners. If you need more clarity, let us talk.
              </p>

              <Button variant="outline" size="lg" className="font-default font-medium tracking-wider uppercase text-xs border-primary text-primary bg-transparent hover:bg-primary hover:text-primary-foreground transition-all duration-300 px-8 py-6 h-auto">
                Contact Us Today
              </Button>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 ease-out delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Accordion type="single" collapsible defaultValue="item-0" className="flex flex-col gap-3">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-0 transition-all duration-300 group" style={{
              backgroundColor: "hsl(0 0% 100%)",
              border: "1px solid hsl(214 15% 88%)",
              boxShadow: "0 4px 20px -2px hsl(222 47% 11% / 0.05)"
            }}>
                  <AccordionTrigger className="hover:no-underline px-6 md:px-8 py-6 md:py-7 min-h-[96px] gap-6 [&>svg]:hidden group-hover:[border-color:hsl(43_72%_52%)]">
                    <div className="flex items-center gap-6 md:gap-8 flex-1 text-left">
                      <span className="font-default font-bold text-xs text-primary shrink-0 tracking-wider">
                        Q.{String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-default font-medium text-base md:text-lg text-foreground leading-snug">
                        {faq.question}
                      </span>
                    </div>
                    <Plus className="size-6 text-primary shrink-0 transition-transform duration-300 group-data-[state=open]:rotate-45" strokeWidth={1.5} />
                  </AccordionTrigger>
                  <AccordionContent className="px-6 md:px-8 pb-8 pt-0">
                    <div className="pl-0 md:pl-[88px]">
                      <p className="font-sans text-base md:text-[1.0625rem]" style={{
                    lineHeight: "1.75",
                    maxWidth: "600px",
                    color: "hsl(215 16% 40%)"
                  }}>
                        {faq.answer}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}